/**
 * Staff Dashboard widget: offline sales that synced with a stock conflict and
 * still need a manager's decision. Stays hidden when the queue is clean, and
 * shows up loudly (with the longest wait) the moment something lands.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { CloudOff, ArrowRight, AlertTriangle } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const waitingFor = (iso) => {
  if (!iso) return 'unknown';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  return hours < 24 ? `${hours}h ${mins % 60}m` : `${Math.floor(hours / 24)}d ${hours % 24}h`;
};

export default function SyncConflictsWidget() {
  const { user } = useAuth();
  const canReview = ['super_admin', 'admin', 'inventory_manager'].includes(user?.role)
    || (Array.isArray(user?.custom_permissions) && user.custom_permissions.includes('inventory_manage'));
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!canReview) return;
    let alive = true;
    api.get('/sync/conflicts', { params: { status: 'open', limit: 5 } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [canReview]);

  const open = data?.open_count || 0;
  if (!canReview || open === 0) return null;

  const rows = data.conflicts || [];
  const oldest = rows[rows.length - 1]?.synced_at;

  return (
    <Card
      data-testid="sync-conflicts-widget"
      className="border-amber-300 bg-gradient-to-br from-amber-50 to-white shadow-[0_4px_24px_-8px_rgba(217,119,6,0.3)]"
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <span className="h-9 w-9 rounded-xl bg-amber-100 text-amber-700 grid place-items-center shrink-0">
              <CloudOff className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                Offline sales need review
                <Badge className="rounded-full bg-amber-100 text-amber-900 border-amber-200 hover:bg-amber-100 text-[10px]" data-testid="sync-widget-count">
                  {open} waiting
                </Badge>
              </div>
              <p className="text-xs text-slate-600 mt-0.5" data-testid="sync-widget-oldest">
                Longest wait {waitingFor(oldest)} · stock was short when these synced
              </p>
            </div>
          </div>
          <Button asChild size="sm" className="rounded-full gap-1.5 bg-amber-600 hover:bg-amber-700" data-testid="sync-widget-review-btn">
            <Link to="/sync-conflicts">Review now <ArrowRight className="h-3 w-3" /></Link>
          </Button>
        </div>
        <ul className="mt-3 space-y-1">
          {rows.slice(0, 3).map((row) => (
            <li
              key={row.sale_id}
              className="flex items-center justify-between gap-2 text-[11px] text-slate-600 px-2.5 py-1.5 rounded-lg bg-white/70 border border-amber-100"
              data-testid={`sync-widget-row-${row.sale_id}`}
            >
              <span className="truncate font-mono">{row.sale_id}</span>
              <span className="shrink-0 flex items-center gap-1 text-amber-700">
                <AlertTriangle className="h-3 w-3" />
                {(row.conflicts || []).length} item{(row.conflicts || []).length === 1 ? '' : 's'} short
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
